import {
  Modal,
  Platform,
  Pressable,
  Text,
  View,
  useWindowDimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { twMerge } from 'tailwind-merge';
import { useColors } from '../lib/colors';
import { useIsDesktop } from '../hooks/useIsDesktop';

interface ResponsiveModalProps {
  visible: boolean;
  onClose: () => void;
  header: React.ReactNode;
  subtitle?: React.ReactNode;
  children: React.ReactNode;
  maxWidth?: number;
  maxHeight?: number;
  androidMinHeight?: number;
  shellClassName?: string;
  /**
   * Fully hide the screen behind the modal (canvas-colored backdrop instead of
   * a dim overlay). Use when the underlying screen is not meaningful context,
   * e.g. the paywall over a locked-out Settings screen.
   */
  opaqueBackdrop?: boolean;
}

export function ResponsiveModal({
  visible,
  onClose,
  header,
  subtitle,
  children,
  maxWidth = 780,
  maxHeight = 760,
  androidMinHeight = 360,
  shellClassName = '',
  opaqueBackdrop = false,
}: ResponsiveModalProps) {
  const c = useColors();
  const isDesktop = useIsDesktop();
  const { height: windowHeight } = useWindowDimensions();
  const isAndroidSheet = !isDesktop && Platform.OS === 'android';
  const isMobileWebSheet = !isDesktop && Platform.OS === 'web';
  const desktopMaxHeight = Math.min(windowHeight * 0.85, maxHeight);
  const androidSheetHeight = Math.max(
    Math.min(windowHeight * 0.85, maxHeight),
    androidMinHeight,
  );
  const mobileWebSheetHeight = Math.min(windowHeight * 0.9, maxHeight);

  return (
    <Modal
      visible={visible}
      animationType={isDesktop ? 'fade' : 'slide'}
      presentationStyle={Platform.OS === 'ios' ? 'pageSheet' : 'overFullScreen'}
      transparent={Platform.OS !== 'ios'}
      onRequestClose={onClose}
    >
      {Platform.OS !== 'ios' && (
        <Pressable
          className={`absolute inset-0 ${
            opaqueBackdrop ? 'bg-canvas dark:bg-canvas-dark' : 'bg-black/50'
          }`}
          onPress={onClose}
        />
      )}

      <View
        className={
          isDesktop
            ? 'flex-1 items-center justify-center'
            : Platform.OS === 'ios'
              ? 'flex-1'
              : isMobileWebSheet
                ? 'flex-1 justify-end'
                : undefined
        }
        style={
          isAndroidSheet
            ? { position: 'absolute', bottom: 0, left: 0, right: 0 }
            : undefined
        }
        pointerEvents="box-none"
      >
        <View
          className={twMerge(
            `bg-white dark:bg-gray-900 ${
              isDesktop
                ? 'rounded-2xl w-full'
                : Platform.OS === 'ios'
                  ? 'flex-1'
                  : 'w-full rounded-t-2xl'
            }`,
            shellClassName,
          )}
          style={
            isDesktop
              ? {
                  maxWidth,
                  maxHeight: desktopMaxHeight,
                  alignSelf: 'center',
                }
              : isAndroidSheet
                ? { height: androidSheetHeight }
                : isMobileWebSheet
                  ? { height: mobileWebSheetHeight }
                  : undefined
          }
        >
          <View className="flex-row items-center justify-between px-5 pt-5 pb-2">
            <View className="flex-1 pr-3">{header}</View>
            <Pressable
              onPress={onClose}
              className="p-1 rounded-full bg-gray-100 dark:bg-gray-700"
            >
              <Ionicons name="close" size={18} color={c.textSecondary} />
            </Pressable>
          </View>

          {subtitle ? (
            <View className="px-5 pb-2">
              {typeof subtitle === 'string' ? (
                <Text className="text-xs text-gray-400 dark:text-gray-500">
                  {subtitle}
                </Text>
              ) : (
                subtitle
              )}
            </View>
          ) : null}

          {children}
        </View>
      </View>
    </Modal>
  );
}
